/**
 * PineConnector GSAP Animation System
 * Enhanced with SplitText, DrawSVG, ScrambleText (Club GreenSock),
 * plus Flip & Observer (free). Falls back gracefully if plugins missing.
 */
(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  // ─── PLUGIN REGISTRATION ──────────────────────────────────────
  gsap.registerPlugin(ScrollTrigger);
  var hasSplitText = typeof SplitText !== 'undefined';

  if (hasSplitText) gsap.registerPlugin(SplitText);

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

  // ─── DEFAULTS (tuned for snappy feel) ─────────────────────────
  var EASE = 'power2.out';
  var DUR = 0.4;
  var TRIGGER_START = 'top 95%';
  var BLUE = 'rgba(17,100,240,';

  // ─── HELPERS ──────────────────────────────────────────────────

  function createTL(trigger, opts) {
    if (!trigger) return null;
    var cfg = {
      scrollTrigger: { trigger: trigger, start: TRIGGER_START, once: true },
      defaults: { ease: EASE, duration: prefersReducedMotion ? 0 : DUR }
    };
    if (opts) {
      if (opts.start) cfg.scrollTrigger.start = opts.start;
      if (opts.scrub) { cfg.scrollTrigger.scrub = opts.scrub; delete cfg.scrollTrigger.once; }
    }
    return gsap.timeline(cfg);
  }

  function hide(els, props) {
    if (!els || (els.length !== undefined && els.length === 0)) return;
    var base = { opacity: 0 };
    if (props) { for (var k in props) base[k] = props[k]; }
    gsap.set(els, base);
  }

  // Manual word split — fallback when SplitText unavailable
  function splitWordsManual(container, className) {
    if (!container) return [];
    className = className || 'gsap-word';
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.textContent.trim()) textNodes.push(node);
    }
    var allSpans = [];
    textNodes.forEach(function (textNode) {
      var parent = textNode.parentNode;
      var words = textNode.textContent.split(/(\s+)/);
      var frag = document.createDocumentFragment();
      words.forEach(function (word) {
        if (/^\s+$/.test(word)) {
          frag.appendChild(document.createTextNode(word));
        } else if (word) {
          var span = document.createElement('span');
          span.className = className;
          span.style.display = 'inline-block';
          span.textContent = word;
          frag.appendChild(span);
          allSpans.push(span);
        }
      });
      parent.replaceChild(frag, textNode);
    });
    return allSpans;
  }

  // Unified split — SplitText if available, manual fallback
  function splitText(el, type, className) {
    if (!el) return { words: [], split: null };
    type = type || 'words';
    if (hasSplitText) {
      var split = new SplitText(el, { type: type, wordsClass: className || 'gsap-word', linesClass: 'gsap-line', charsClass: 'gsap-char' });
      var targets = type === 'chars' ? split.chars : type === 'lines' ? split.lines : split.words;
      return { words: targets, split: split };
    }
    // Fallback: only word split supported
    return { words: splitWordsManual(el, className), split: null };
  }

  // ─── P0: HERO BANNER (C1–C3) ─────────────────────────────────

  function initHeroBanner() {
    var root = document.querySelector('.new-banner');
    if (!root) return;

    var label = root.querySelector('.section-header');
    var heading = root.querySelector('.banner__heading');
    var desc = root.querySelector('.banner-information > p');
    var buttons = root.querySelector('.button-content');
    var customItems = root.querySelectorAll('.custom-content');
    var image = root.querySelector('.new-banner-image');

    hide(label, { y: 15 });
    hide(desc, { y: 15 });
    hide(buttons, { y: 15 });
    hide(customItems, { y: 10 });

    // C3: 3D image entrance
    if (image) {
      gsap.set(image, {
        opacity: 0, y: 40, scale: 0.96,
        rotationX: prefersReducedMotion ? 0 : 6,
        transformPerspective: 1200,
        transformOrigin: 'center bottom'
      });
    }

    var tl = gsap.timeline({ defaults: { ease: EASE, duration: prefersReducedMotion ? 0 : DUR } });

    hide(heading, { y: 20 });

    tl.to(label, { opacity: 1, y: 0 });
    tl.to(heading, { opacity: 1, y: 0, duration: prefersReducedMotion ? 0 : 0.5 }, '-=0.3');

    tl.to(desc, { opacity: 1, y: 0 }, '-=0.25')
      .to(buttons, { opacity: 1, y: 0 }, '-=0.25')
      .to(customItems, { opacity: 1, y: 0, stagger: 0.06 }, '-=0.2');

    // C3: 3D image entrance
    if (image) {
      tl.to(image, {
        opacity: 1, y: 0, scale: 1, rotationX: 0,
        duration: prefersReducedMotion ? 0 : 0.7,
        ease: 'power2.out'
      }, '-=0.4');
    }

    // Scrub parallax on hero image
    if (!prefersReducedMotion && image) {
      gsap.to(image, {
        y: 60, ease: 'none',
        scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: 1 }
      });
    }
  }

  // ─── P0: BOX ANIMATION (D1) ──────────────────────────────────

  function initBoxAnimation() {
    var root = document.querySelector('.image-animation-section');
    if (!root) return;

    var box1 = root.querySelector('#box-animation1');
    var arrow1 = root.querySelector('#arrow1');
    var box2 = root.querySelector('#box-animation2');
    var arrow2 = root.querySelector('#arrow2');
    var box3 = root.querySelector('#box-animation3');

    hide(box1, { y: 20 });
    hide(arrow1, { scaleX: 0 });
    hide(box2, { y: 20 });
    hide(arrow2, { scaleX: 0 });
    hide(box3, { y: 20 });

    if (arrow1) gsap.set(arrow1, { transformOrigin: 'left center' });
    if (arrow2) gsap.set(arrow2, { transformOrigin: 'left center' });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(box1, { opacity: 1, y: 0 })
      .to(arrow1, { opacity: 1, scaleX: 1, duration: prefersReducedMotion ? 0 : 0.3 })
      .to(box2, { opacity: 1, y: 0 }, '-=0.15')
      .to(arrow2, { opacity: 1, scaleX: 1, duration: prefersReducedMotion ? 0 : 0.3 })
      .to(box3, { opacity: 1, y: 0 }, '-=0.15');

    // D1: One-time blue flow pulse
    if (!prefersReducedMotion) {
      var pulseEls = [box1, arrow1, box2, arrow2, box3].filter(Boolean);
      tl.add(function () {
        var p = gsap.timeline({ delay: 0.3 });
        pulseEls.forEach(function (el, i) {
          p.to(el, { filter: 'drop-shadow(0 0 10px ' + BLUE + '0.5))', duration: 0.2 }, i * 0.25)
           .to(el, { filter: 'drop-shadow(0 0 0px ' + BLUE + '0))', duration: 0.35 }, i * 0.25 + 0.2);
        });
      });
    }
  }

  function initHomeTraders() {
    var root = document.querySelector('.new-home-traders');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var cards = root.querySelectorAll('.traderd-item');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(cards, { y: 25 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.25')
      .to(cards, { opacity: 1, y: 0, stagger: 0.1 });
  }

  function initControlAnalytics() {
    var root = document.querySelector('.control-analytics-section');
    if (!root) return;

    var header = root.querySelector('.section-header');
    var slider = root.querySelector('.section-analytics-slider-main');

    hide(header, { y: 18 });
    hide(slider, { y: 20 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(header, { opacity: 1, y: 0 })
      .to(slider, { opacity: 1, y: 0 }, '-=0.2');
  }

  // ─── P0: PRICING (E1–E4) ─────────────────────────────────────

  function initEconomicalPrice() {
    var root = document.querySelector('.section-economical-price');
    if (!root) return;

    var header = root.querySelector('.section-header');
    var cards = root.querySelectorAll('.economic--price-grid');
    var trialStrip = root.querySelector('.pricing-trial-strip');
    var proof = root.querySelector('.pricing-social-proof');
    var popularBadge = root.querySelector('.popular-badge');

    hide(header, { y: 18 });
    hide(cards, { y: 25 });
    hide(trialStrip, { y: 20 });
    var proofCards = root.querySelectorAll('.pricing-testimonial');
    var proofStars = root.querySelectorAll('.pricing-testimonial-stars');

    hide(proofCards, { y: 20 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(header, { opacity: 1, y: 0 });

    tl.to(cards, { opacity: 1, y: 0, stagger: 0.08 });

    tl.to(trialStrip, { opacity: 1, y: 0 }, '-=0.2');

    // Pricing social proof — staggered card entrance + star shimmer
    if (proofCards.length) {
      tl.to(proofCards, {
        opacity: 1, y: 0,
        stagger: 0.12,
        duration: 0.45,
        ease: 'power3.out'
      }, '-=0.2');

      if (!prefersReducedMotion && proofStars.length) {
        tl.to(proofStars, {
          color: '#fbbf24',
          textShadow: '0 0 8px rgba(251,191,36,0.4)',
          stagger: 0.1,
          duration: 0.3,
          ease: 'power2.out',
          onComplete: function () {
            gsap.to(proofStars, { textShadow: '0 0 0px rgba(251,191,36,0)', duration: 1 });
          }
        }, '-=0.3');
      }
    }

    // E3: Popular badge pulse
    if (popularBadge && !prefersReducedMotion) {
      tl.add(function () {
        gsap.to(popularBadge, {
          scale: 1.05,
          boxShadow: '0 4px 20px ' + BLUE + '0.45)',
          duration: 0.3, ease: 'power2.inOut',
          yoyo: true, repeat: 5
        });
      });
    }

  }

  function initHomeJoinTraders() {
    var root = document.querySelector('.section-join-traders');
    if (!root) return;

    var img = root.querySelector('.client--img');
    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var btn = root.querySelector('.traders_btn');
    var time = root.querySelector('.traders_time');
    var wrapper = root.querySelector('.traders-wrapper');

    hide(img, { scale: 0.92 });
    hide(label, { y: 12 });
    hide(title, { y: 15 });
    hide(cnt, { y: 12 });
    hide(btn, { y: 12 });
    hide(time);
    hide(wrapper, { y: 25, scale: 0.97 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(img, { opacity: 1, scale: 1 })
      .to(label, { opacity: 1, y: 0 }, '-=0.25')
      .to(title, { opacity: 1, y: 0 }, '-=0.2')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(btn, { opacity: 1, y: 0 }, '-=0.15')
      .to(time, { opacity: 1 }, '-=0.15')
      .to(wrapper, { opacity: 1, y: 0, scale: 1, duration: prefersReducedMotion ? 0 : 0.55 }, '-=0.25');
  }

  // ─── P1 SECTIONS ──────────────────────────────────────────────

  function initEscapeChaos() {
    var root = document.querySelector('.escape-chaos-section');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var left = root.querySelectorAll('.left-block .icon-wrapper');
    var right = root.querySelectorAll('.right-block .icon-wrapper');
    var leftBlock = root.querySelector('.left-block .inner-block');
    var rightBlock = root.querySelector('.right-block .inner-block');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(leftBlock, { x: -20 });
    hide(rightBlock, { x: 20 });
    hide(left, { x: -20 });
    hide(right, { x: 20 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(leftBlock, { opacity: 1, x: 0 }, '-=0.15')
      .to(rightBlock, { opacity: 1, x: 0 }, '<')
      .to(left, { opacity: 1, x: 0, stagger: 0.06 }, '-=0.2')
      .to(right, { opacity: 1, x: 0, stagger: 0.06 }, '<');
  }

  function initOneStopSolution() {
    var root = document.querySelector('.one-stop-image-text');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var boxes = root.querySelectorAll('.box');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(boxes, { y: 25 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(boxes, { opacity: 1, y: 0, stagger: 0.07 });
  }

  function initSolutionSteps() {
    var roots = document.querySelectorAll('.new-dedicated-support .home-solution-grid');
    if (!roots.length) return;

    roots.forEach(function (grid) {
      var section = grid.closest('.new-dedicated-support');
      if (!section || section.dataset.gsapInit) return;
      section.dataset.gsapInit = 'true';

      var label = section.querySelector('.section_label');
      var title = section.querySelector('.section_title');
      var cnt = section.querySelector('.section_cnt');
      var steps = section.querySelectorAll('.home-solution-step');

      hide(label, { y: 15 });
      hide(title, { y: 18 });
      hide(cnt, { y: 15 });
      hide(steps, { y: 20 });

      var tl = createTL(section);
      if (!tl) return;

      tl.to(label, { opacity: 1, y: 0 })
        .to(title, { opacity: 1, y: 0 }, '-=0.25')
        .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
        .to(steps, { opacity: 1, y: 0, stagger: 0.05 });
    });
  }

  function initTakeControl() {
    var root = document.querySelector('.take-control-section');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var image = root.querySelector('.bottom-image') || root.querySelector('.img');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(image, { y: 30, scale: 0.97 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(image, { opacity: 1, y: 0, scale: 1, duration: prefersReducedMotion ? 0 : 0.55 });

    if (!prefersReducedMotion && image) {
      gsap.to(image, {
        y: 40, ease: 'none',
        scrollTrigger: { trigger: root, start: 'top 30%', end: 'bottom top', scrub: 1 }
      });
    }
  }

  function initPredesignTask() {
    var root = document.querySelector('.predesign-task');
    if (!root) return;

    var icons = root.querySelector('.predesign-head-icons');
    var title = root.querySelector('.section_title') || root.querySelector('.predesign-head h2');
    var cnt = root.querySelector('.section_cnt') || root.querySelector('.predesign-head p');
    var cards = root.querySelectorAll('.predesign-types-main-inner');

    hide(icons, { scale: 0.92 });
    hide(title, { y: 15 });
    hide(cnt, { y: 12 });
    hide(cards, { y: 20 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(icons, { opacity: 1, scale: 1 })
      .to(title, { opacity: 1, y: 0 }, '-=0.2')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(cards, { opacity: 1, y: 0, stagger: 0.06 });
  }

  function initRealTimeAlerts() {
    var root = document.querySelector('.realtime-alerts-section');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var alertCard = root.querySelector('.alert-part-main');
    var types = root.querySelectorAll('.alert-types-main > div');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(alertCard, { y: 20 });
    hide(types, { y: 18 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(alertCard, { opacity: 1, y: 0 })
      .to(types, { opacity: 1, y: 0, stagger: 0.07 }, '-=0.15');
  }

  function initCustomQuote() {
    var root = document.querySelector('.quote-wrapper.quote');
    if (!root) return;

    var header = root.querySelector('.section-header');
    var slider = root.querySelector('.card__wrap--inner-quote') || root.querySelector('.custom-quote-slider');

    hide(header, { y: 18 });
    hide(slider, { y: 20 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(header, { opacity: 1, y: 0 })
      .to(slider, { opacity: 1, y: 0 }, '-=0.2');
  }

  // ─── P1: VP QUOTE — Typewriter cinematic reveal ─────────────

  function initVPQuote() {
    var root = document.querySelector('.vp-section');
    if (!root) return;

    var mark = root.querySelector('.vp-mark');
    var quote = root.querySelector('.vp-quote');
    var accentLine = root.querySelector('.vp-accent-line');
    var authorBlock = root.querySelector('.vp-author-block');

    if (!quote) return;

    // ── Highlight "never notice it" in blue (before SplitText runs) ──
    quote.innerHTML = quote.innerHTML.replace(
      /(never notice it)/gi,
      '<span class="vp-blue">$1</span>'
    );

    // ── Create left vertical accent line ──
    var accentLeft = document.createElement('div');
    accentLeft.className = 'vp-accent-left';
    quote.appendChild(accentLeft);

    // ── Create blinking cursor ──
    var cursor = document.createElement('span');
    cursor.className = 'vp-cursor';
    cursor.textContent = '\u258C';
    quote.appendChild(cursor);

    // ── Collect real text paragraphs (skip empty <p> tags) ──
    var allParas = quote.querySelectorAll('p');
    var textParas = [];
    allParas.forEach(function (p) {
      if (p.textContent.trim().length > 1) textParas.push(p);
    });

    // ── SplitText each paragraph into chars ──
    var splits = [];
    var allChars = [];
    if (hasSplitText && textParas.length && !prefersReducedMotion) {
      textParas.forEach(function (p) {
        var s = new SplitText(p, { type: 'chars', charsClass: 'vp-char' });
        splits.push(s);
        allChars = allChars.concat(Array.from(s.chars));
      });
    }

    // ── Initial states ──
    gsap.set(mark, { scale: 0, opacity: 0, transformOrigin: 'center center' });
    gsap.set(accentLeft, { scaleY: 0 });
    if (accentLine) gsap.set(accentLine, { scaleX: 0, transformOrigin: 'center center' });
    gsap.set(authorBlock, { y: 15, opacity: 0 });

    if (allChars.length) {
      gsap.set(allChars, { opacity: 0 });
    } else {
      gsap.set(quote, { opacity: 0, y: 20 });
    }

    // ── Timeline — fires once when section is 20% in viewport ──
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top 80%',
        once: true
      },
      defaults: { ease: 'power2.out' }
    });

    if (prefersReducedMotion) {
      gsap.set([mark, accentLeft, accentLine, authorBlock, quote], { clearProps: 'all' });
      cursor.remove();
      return;
    }

    // Step 1: Quote mark — scale up with playful overshoot
    tl.to(mark, {
      scale: 1, opacity: 0.85,
      duration: 0.4, ease: 'back.out(1.7)'
    }, 0);

    // Step 2: Left accent line draws down
    tl.to(accentLeft, {
      scaleY: 1,
      duration: 0.6, ease: 'power3.out'
    }, 0.2);

    // Step 3: Typewriter — chars appear one by one
    if (allChars.length) {
      tl.to(allChars, {
        opacity: 1,
        stagger: 0.02,
        duration: 0.01,
        ease: 'none'
      }, 0.5);

      var typeDone = 0.5 + allChars.length * 0.02;

      // Blue phrase glow pulse after typing completes
      var blueSpans = quote.querySelectorAll('.vp-blue');
      if (blueSpans.length) {
        tl.fromTo(blueSpans,
          { textShadow: '0 0 0px rgba(17,100,240,0)' },
          { textShadow: '0 0 24px rgba(17,100,240,0.6)', duration: 0.5 },
          typeDone
        );
        tl.to(blueSpans,
          { textShadow: '0 0 0px rgba(17,100,240,0)', duration: 1.2 },
          typeDone + 0.6
        );
      }

      // Bold text brightness pulse
      var strongChars = quote.querySelectorAll('strong .vp-char');
      if (strongChars.length) {
        tl.fromTo(strongChars,
          { textShadow: '0 0 0px rgba(255,255,255,0)' },
          { textShadow: '0 0 18px rgba(255,255,255,0.35)', duration: 0.4, stagger: 0.008 },
          typeDone + 0.15
        );
        tl.to(strongChars,
          { textShadow: '0 0 0px rgba(255,255,255,0)', duration: 1 },
          typeDone + 0.8
        );
      }

      // Cursor: blink during typing (CSS handles it), then fade out
      tl.to(cursor, { opacity: 0, duration: 0.6 }, typeDone + 0.8);
    } else {
      // Fallback: fade whole quote
      tl.to(quote, { opacity: 1, y: 0, duration: 0.8 }, 0.2);
      cursor.remove();
    }

    // Horizontal accent line draw
    if (accentLine) {
      tl.to(accentLine, {
        scaleX: 1,
        duration: 0.45, ease: 'power3.out'
      }, '>0.1');
    }

    // Author block — lands last with bounce
    tl.to(authorBlock, {
      y: 0, opacity: 1,
      duration: 0.5, ease: 'back.out(1.4)'
    }, '>0.1');

    // Parallax on quote mark while scrolling
    gsap.to(mark, {
      y: -30, ease: 'none',
      scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: 1.5 }
    });
  }

  function initImageWithStep() {
    var root = document.querySelector('.section-image-step');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var steps = root.querySelectorAll('.step_cnt');
    var image = root.querySelector('.step-image_wrapper');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(steps, { x: -20 });
    hide(image, { x: 25 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(steps, { opacity: 1, x: 0, stagger: 0.06 })
      .to(image, { opacity: 1, x: 0, duration: prefersReducedMotion ? 0 : 0.5 }, '<');
  }

  function initHomeSupport() {
    var roots = document.querySelectorAll('.new-dedicated-support');
    roots.forEach(function (root) {
      if (root.querySelector('.home-solution-grid')) return;
      if (root.dataset.gsapInit) return;
      root.dataset.gsapInit = 'true';

      var label = root.querySelector('.section_label');
      var title = root.querySelector('.section_title');
      var left = root.querySelector('.dedicated-support-left');
      var right = root.querySelector('.dedicated-support-right');
      var counters = root.querySelectorAll('.dedicated-counter');

      if (!left && !right) return;

      hide(label, { y: 15 });
      hide(title, { y: 18 });
      hide(left, { x: -20 });
      hide(right, { x: 20 });
      hide(counters, { y: 15 });

      var tl = createTL(root);
      if (!tl) return;

      tl.to(label, { opacity: 1, y: 0 })
        .to(title, { opacity: 1, y: 0 }, '-=0.25')
        .to(left, { opacity: 1, x: 0 })
        .to(right, { opacity: 1, x: 0 }, '<')
        .to(counters, { opacity: 1, y: 0, stagger: 0.06 }, '-=0.2');
    });
  }

  function initPrioritySuccess() {
    var root = document.querySelector('.priority-success-section');
    if (!root) return;

    var label = root.querySelector('.section_label');
    var title = root.querySelector('.section_title');
    var cnt = root.querySelector('.section_cnt');
    var cards = root.querySelectorAll('.right-block .wrapper');

    hide(label, { y: 15 });
    hide(title, { y: 18 });
    hide(cnt, { y: 15 });
    hide(cards, { y: 18 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(label, { opacity: 1, y: 0 })
      .to(title, { opacity: 1, y: 0 }, '-=0.25')
      .to(cnt, { opacity: 1, y: 0 }, '-=0.2')
      .to(cards, { opacity: 1, y: 0, stagger: 0.06 });
  }

  // ─── P2 SECTIONS ──────────────────────────────────────────────

  function initRotatingBar() {
    var root = document.querySelector('.rotate-bar-section') || document.querySelector('.rotating-bar-section');
    if (!root) return;

    var header = root.querySelector('.rotate-bar-header');
    var footer = root.querySelector('.rotate-bar-footer');
    var marquee = root.querySelector('.rotating-bar-wrapper');

    hide(header, { y: 15 });
    if (marquee) hide(marquee, { y: 10 });
    if (footer) hide(footer, { y: 15 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(header, { opacity: 1, y: 0 });
    if (marquee) tl.to(marquee, { opacity: 1, y: 0 }, '-=0.3');
    if (footer) tl.to(footer, { opacity: 1, y: 0 }, '-=0.2');
  }

  // ─── P2: TESTIMONIALS MARQUEE — Flowy Showstopper ──────────

  function initTestimonialsMarquee() {
    var root = document.querySelector('.tm-section');
    if (!root) return;

    var label = root.querySelector('.tm-header .section_label');
    var title = root.querySelector('.tm-title');
    var sub = root.querySelector('.tm-sub');
    var trackWraps = root.querySelectorAll('.tm-track-wrap');
    var cards = root.querySelectorAll('.tm-card');
    var cardTexts = root.querySelectorAll('.tm-card-text');
    var cardFooters = root.querySelectorAll('.tm-card-footer');
    var quoteSvgs = root.querySelectorAll('.tm-quote-svg');
    var quoteSvgPaths = root.querySelectorAll('.tm-quote-svg path');

    // ── Initial states ──────────────────────────────────────────
    // Header: title gets cinematic focus-pull, others simple fade
    if (label) gsap.set(label, { opacity: 0, y: 12 });
    if (title) gsap.set(title, { opacity: 0, scale: 0.94, filter: prefersReducedMotion ? 'none' : 'blur(8px)' });
    if (sub) gsap.set(sub, { opacity: 0, y: 10 });

    if (!prefersReducedMotion) {
      // Track wraps converge: row 1 rises from below, row 2 drops from above
      trackWraps.forEach(function (wrap, i) {
        gsap.set(wrap, { opacity: 0, y: i === 0 ? 50 : -35 });
      });
      // Card internals hidden for layered reveal
      gsap.set(quoteSvgs, { opacity: 0, scale: 0.4, rotation: -12 });
      gsap.set(cardTexts, { opacity: 0, y: 10 });
      gsap.set(cardFooters, { opacity: 0, x: -10 });
    }

    // ── Timeline ────────────────────────────────────────────────
    var tl = createTL(root);
    if (!tl) return;

    // Phase 1: Header — cinematic focus pull
    if (label) {
      tl.to(label, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' });
    }
    tl.to(title, {
      opacity: 1, scale: 1, filter: 'blur(0px)',
      duration: prefersReducedMotion ? 0 : 0.8,
      ease: 'power3.out',
      clearProps: 'filter'
    }, label ? '-=0.25' : '0');
    if (sub) {
      tl.to(sub, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, '-=0.5');
    }

    // Phase 2: Tracks converge from opposite directions
    // CSS scroll animation keeps running — we only animate the wrapper's y + opacity
    // power4.out = fast launch, ultra-smooth settle (no abrupt stop)
    if (!prefersReducedMotion && trackWraps.length) {
      trackWraps.forEach(function (wrap, i) {
        tl.to(wrap, {
          opacity: 1, y: 0,
          duration: 1,
          ease: 'power4.out'
        }, i === 0 ? '-=0.35' : '-=0.8');
      });

      // Phase 3: Card internals — organic layered reveal
      // Quote marks pop in with playful overshoot + random order
      tl.to(quoteSvgs, {
        opacity: 0.6, scale: 1, rotation: 0,
        stagger: { each: 0.012, from: 'random' },
        duration: 0.4, ease: 'back.out(2)'
      }, '-=0.6');

      // Card text rises in — random stagger for organic feel
      tl.to(cardTexts, {
        opacity: 1, y: 0,
        stagger: { each: 0.02, from: 'random' },
        duration: 0.45, ease: 'power2.out'
      }, '-=0.35');

      // Card footers slide in from left — edges-first stagger
      tl.to(cardFooters, {
        opacity: 1, x: 0,
        stagger: { each: 0.015, from: 'edges' },
        duration: 0.4, ease: 'power3.out'
      }, '-=0.35');
    }

    // ── Blue tint + breathing on quote SVGs ─────────────────────
    if (!prefersReducedMotion && quoteSvgPaths.length) {
      quoteSvgPaths.forEach(function (path) {
        gsap.set(path, { fill: BLUE + '0.25)' });
      });
      // Slow breathing — staggered start per SVG
      quoteSvgs.forEach(function (svg, i) {
        gsap.to(svg, {
          opacity: 0.75, duration: 2.5, ease: 'sine.inOut',
          yoyo: true, repeat: -1, delay: i * 0.12
        });
      });
    }

    // ── Card hover: glow + micro-rotation + elastic snap-back ───
    if (!prefersReducedMotion) {
      cards.forEach(function (card) {
        card.addEventListener('mouseenter', function () {
          gsap.to(card, {
            scale: 1.04, rotation: 0.5,
            borderColor: BLUE + '0.45)',
            boxShadow: '0 8px 30px ' + BLUE + '0.14)',
            duration: 0.3, ease: 'power2.out', overwrite: 'auto'
          });
        });
        card.addEventListener('mouseleave', function () {
          gsap.to(card, {
            scale: 1, rotation: 0,
            borderColor: 'rgba(255,255,255,0.06)',
            boxShadow: '0 0 0px ' + BLUE + '0)',
            duration: 0.55, ease: 'elastic.out(1, 0.6)', overwrite: 'auto'
          });
        });
      });
    }

    /* Spotlight pulse removed — setInterval replaced with one-time stagger */
    if (!prefersReducedMotion && cards.length) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.unobserve(root);
            var shuffled = gsap.utils.shuffle(cards.slice());
            shuffled.forEach(function (c, i) {
              gsap.to(c, {
                borderColor: BLUE + '0.5)', duration: 0.4, ease: 'power2.in',
                delay: i * 0.6,
                onComplete: function () {
                  gsap.to(c, { borderColor: 'rgba(255,255,255,0.06)', duration: 0.8, ease: 'power2.out' });
                }
              });
            });
          }
        });
      }, { threshold: 0.1 });
      observer.observe(root);
    }
  }

  function initPlanFeature() {
    var root = document.querySelector('.section-home-plan-feature');
    if (!root) return;

    var header = root.querySelector('.section-header');
    var rows = root.querySelectorAll('.compare-row, tbody tr');

    hide(header, { y: 18 });
    hide(rows, { y: 8 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(header, { opacity: 1, y: 0 })
      .to(rows, { opacity: 1, y: 0, stagger: 0.03 });
  }

  function initFAQ() {
    var root = document.querySelector('.faq_block_wrap');
    if (!root) return;

    var header = root.querySelector('.section-header');
    var items = root.querySelectorAll('.bc-list > li');

    hide(header, { y: 18 });
    hide(items, { y: 10 });

    var tl = createTL(root);
    if (!tl) return;

    tl.to(header, { opacity: 1, y: 0 })
      .to(items, { opacity: 1, y: 0, stagger: 0.04 });
  }

  // ─── MICRO-INTERACTIONS — Make the page feel alive ──────────

  // Reusable: GSAP hover with blue glow + lift
  function addGlowHover(selector, opts) {
    if (prefersReducedMotion) return;
    opts = opts || {};
    var s = opts.scale || 1.03;
    var glow = opts.glow !== false;
    document.querySelectorAll(selector).forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        gsap.to(el, {
          scale: s,
          borderColor: glow ? BLUE + '0.35)' : undefined,
          boxShadow: glow ? '0 8px 24px ' + BLUE + '0.1)' : undefined,
          duration: 0.25, ease: 'power2.out', overwrite: 'auto'
        });
      });
      el.addEventListener('mouseleave', function () {
        gsap.to(el, {
          scale: 1,
          borderColor: glow ? 'rgba(255,255,255,0.06)' : undefined,
          boxShadow: glow ? '0 0 0px ' + BLUE + '0)' : undefined,
          duration: 0.4, ease: 'elastic.out(1, 0.6)', overwrite: 'auto'
        });
      });
    });
  }

  function initMicroInteractions() {
    if (prefersReducedMotion) return;

    // ── Priority success cards: arrow shift + icon lift ──
    document.querySelectorAll('.priority-success-section .wrapper').forEach(function (card) {
      var arrow = card.querySelector('svg');
      var icon = card.querySelector('.icon');
      card.addEventListener('mouseenter', function () {
        if (arrow) gsap.to(arrow, { x: 5, duration: 0.25, ease: 'power2.out' });
        if (icon) gsap.to(icon, { y: -2, scale: 1.08, duration: 0.25, ease: 'power2.out' });
        gsap.to(card, { borderColor: BLUE + '0.3)', boxShadow: '0 8px 24px ' + BLUE + '0.08)', duration: 0.25, overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        if (arrow) gsap.to(arrow, { x: 0, duration: 0.3, ease: 'power2.out' });
        if (icon) gsap.to(icon, { y: 0, scale: 1, duration: 0.3, ease: 'power2.out' });
        gsap.to(card, { borderColor: 'rgba(255,255,255,0.06)', boxShadow: '0 0 0 transparent', duration: 0.4, ease: 'elastic.out(1,0.6)', overwrite: 'auto' });
      });
    });

    // ── Pricing cards: 3D tilt (desktop) / scale (touch) ──
    if (!isTouchDevice) {
      document.querySelectorAll('.economic--price-grid').forEach(function (card) {
        card.addEventListener('mousemove', function (e) {
          var rect = card.getBoundingClientRect();
          gsap.to(card, {
            rotateY: ((e.clientX - rect.left - rect.width / 2) / (rect.width / 2)) * 4,
            rotateX: -((e.clientY - rect.top - rect.height / 2) / (rect.height / 2)) * 4,
            boxShadow: '0 12px 30px ' + BLUE + '0.12)',
            duration: 0.25, ease: 'power2.out', overwrite: 'auto'
          });
        });
        card.addEventListener('mouseleave', function () {
          gsap.to(card, { rotateX: 0, rotateY: 0, boxShadow: '0 0 0 transparent', duration: 0.5, ease: 'elastic.out(1,0.5)', overwrite: 'auto' });
        });
      });
    } else {
      addGlowHover('.economic--price-grid', { scale: 1.02 });
    }

    // ── Pricing CTA buttons: glow pulse on hover ──
    document.querySelectorAll('a.eco-plan-btn').forEach(function (btn) {
      btn.addEventListener('mouseenter', function () {
        gsap.killTweensOf(btn);
        gsap.to(btn, { boxShadow: '0 0 20px ' + BLUE + '0.25)', scale: 1.03, duration: 0.15, ease: 'power2.out' });
      });
      btn.addEventListener('mouseleave', function () {
        gsap.killTweensOf(btn);
        gsap.to(btn, { boxShadow: '0 0 0 transparent', scale: 1, x: 0, y: 0, duration: 0.2, ease: 'power2.out' });
      });
      btn.addEventListener('mousemove', function (e) { e.stopPropagation(); });
    });

    // ── Pricing social proof mini-testimonials: hover brighten ──
    document.querySelectorAll('.pricing-testimonial').forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        gsap.to(card, { borderColor: BLUE + '0.25)', scale: 1.02, duration: 0.25, ease: 'power2.out', overwrite: 'auto' });
        var text = card.querySelector('.pricing-testimonial-text');
        if (text) gsap.to(text, { color: '#fff', duration: 0.2, overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(card, { borderColor: 'rgba(255,255,255,0.06)', scale: 1, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
        var text = card.querySelector('.pricing-testimonial-text');
        if (text) gsap.to(text, { color: '#ccc', duration: 0.3, overwrite: 'auto' });
      });
    });

    // ── Trader cards: lift + icon glow ──
    document.querySelectorAll('.traderd-item').forEach(function (card) {
      var img = card.querySelector('.traderd-img-grid img, .traderd-img-grid svg');
      card.addEventListener('mouseenter', function () {
        gsap.to(card, { y: -4, borderColor: BLUE + '0.3)', boxShadow: '0 12px 24px ' + BLUE + '0.08)', duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
        if (img) gsap.to(img, { scale: 1.06, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(card, { y: 0, borderColor: 'rgba(255,255,255,0.06)', boxShadow: '0 0 0 transparent', duration: 0.4, ease: 'elastic.out(1,0.6)', overwrite: 'auto' });
        if (img) gsap.to(img, { scale: 1, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      });
    });

    // ── Solution steps: lift + left blue accent slide ──
    document.querySelectorAll('.home-solution-step').forEach(function (step) {
      step.addEventListener('mouseenter', function () {
        gsap.to(step, { y: -3, borderColor: BLUE + '0.35)', boxShadow: '0 8px 20px ' + BLUE + '0.08)', duration: 0.25, ease: 'power2.out', overwrite: 'auto' });
      });
      step.addEventListener('mouseleave', function () {
        gsap.to(step, { y: 0, borderColor: 'rgba(255,255,255,0.06)', boxShadow: '0 0 0 transparent', duration: 0.4, ease: 'elastic.out(1,0.6)', overwrite: 'auto' });
      });
    });

    // ── Predesign task cards: lift + icon scale ──
    document.querySelectorAll('.predesign-types-main-inner').forEach(function (card) {
      var icon = card.querySelector('img');
      card.addEventListener('mouseenter', function () {
        gsap.to(card, { y: -3, borderColor: BLUE + '0.35)', boxShadow: '0 8px 20px ' + BLUE + '0.08)', duration: 0.25, ease: 'power2.out', overwrite: 'auto' });
        if (icon) gsap.to(icon, { scale: 1.1, rotation: 3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(card, { y: 0, borderColor: 'rgba(255,255,255,0.06)', boxShadow: '0 0 0 transparent', duration: 0.4, ease: 'elastic.out(1,0.6)', overwrite: 'auto' });
        if (icon) gsap.to(icon, { scale: 1, rotation: 0, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      });
    });

    // ── Escape chaos icon wrappers: lift + icon breathe ──
    document.querySelectorAll('.escape-chaos-section .icon-wrapper').forEach(function (wrap) {
      var icon = wrap.querySelector('.icon img, .icon svg');
      wrap.addEventListener('mouseenter', function () {
        gsap.to(wrap, { y: -3, borderColor: BLUE + '0.35)', boxShadow: '0 8px 20px ' + BLUE + '0.08)', duration: 0.25, ease: 'power2.out', overwrite: 'auto' });
        if (icon) gsap.to(icon, { scale: 1.12, duration: 0.3, ease: 'back.out(1.4)', overwrite: 'auto' });
      });
      wrap.addEventListener('mouseleave', function () {
        gsap.to(wrap, { y: 0, borderColor: 'rgba(255,255,255,0.05)', boxShadow: '0 0 0 transparent', duration: 0.4, ease: 'elastic.out(1,0.6)', overwrite: 'auto' });
        if (icon) gsap.to(icon, { scale: 1, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      });
    });

    // ── One-stop solution boxes: lift + shadow ──
    document.querySelectorAll('.one-stop-image-text .box').forEach(function(el){
      el.addEventListener('mouseenter',function(){
        gsap.to(el,{scale:1.03,borderColor:BLUE+'0.55)',boxShadow:'0 0 0 1px '+BLUE+'0.3), 0 8px 32px '+BLUE+'0.15)',duration:0.25,ease:'power2.out',overwrite:'auto'});
      });
      el.addEventListener('mouseleave',function(){
        gsap.to(el,{scale:1,borderColor:'rgba(255,255,255,0.05)',boxShadow:'0 0 0px '+BLUE+'0)',duration:0.4,ease:'elastic.out(1,0.6)',overwrite:'auto'});
      });
    });

    // ── Image-with-step items: lift + blue accent ──
    addGlowHover('.step_cnt');

    // ── FAQ accordion items: expand hint + blue accent ──
    document.querySelectorAll('.bc-list > li').forEach(function (item) {
      var link = item.querySelector('a');
      var arrow = item.querySelector('i, svg, .faq-arrow');
      item.addEventListener('mouseenter', function () {
        gsap.to(item, { x: 4, borderColor: BLUE + '0.2)', duration: 0.2, ease: 'power2.out', overwrite: 'auto' });
        if (link) gsap.to(link, { color: '#fff', duration: 0.2, overwrite: 'auto' });
        if (arrow) gsap.to(arrow, { rotation: 45, scale: 1.1, duration: 0.25, ease: 'power2.out', overwrite: 'auto' });
      });
      item.addEventListener('mouseleave', function () {
        gsap.to(item, { x: 0, borderColor: 'rgba(255,255,255,0.06)', duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
        if (link) gsap.to(link, { color: '', duration: 0.3, overwrite: 'auto' });
        if (arrow) gsap.to(arrow, { rotation: 0, scale: 1, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
    });

    // ── Magnetic button effect (desktop only, exclude pricing buttons) ──
    if (!isTouchDevice) {
      document.querySelectorAll('.hover-btn, .start-free-trial').forEach(function (btn) {
        btn.addEventListener('mousemove', function (e) {
          var rect = btn.getBoundingClientRect();
          var ox = Math.max(-4, Math.min(4, (e.clientX - rect.left - rect.width / 2) * 0.1));
          var oy = Math.max(-4, Math.min(4, (e.clientY - rect.top - rect.height / 2) * 0.1));
          gsap.to(btn, { x: ox, y: oy, duration: 0.2, ease: 'power2.out', overwrite: 'auto' });
        });
        btn.addEventListener('mouseleave', function () {
          gsap.to(btn, { x: 0, y: 0, duration: 0.4, ease: 'elastic.out(1,0.5)', overwrite: 'auto' });
        });
      });
    }

    // ── Section labels: subtle glow on hover ──
    document.querySelectorAll('.section_label').forEach(function (label) {
      label.addEventListener('mouseenter', function () {
        gsap.to(label, { textShadow: '0 0 12px ' + BLUE + '0.5)', duration: 0.25, overwrite: 'auto' });
      });
      label.addEventListener('mouseleave', function () {
        gsap.to(label, { textShadow: '0 0 0px ' + BLUE + '0)', duration: 0.4, overwrite: 'auto' });
      });
    });

    // ── Trial strip options: glow hover ──
    // Edge card keeps its own blue border/shadow — glow:false so mouseleave can't overwrite them with defaults
    addGlowHover('.trial-option:not(.trial-option-edge)', { scale: 1.01 });
    addGlowHover('.trial-option-edge', { scale: 1.01, glow: false });
  }

  // ─── AMBIENT BREATHING — Perpetual subtle life ────────────────

  function initAmbientLife() {
    if (prefersReducedMotion) return;

    // Popular badge: glow handled by CSS to avoid non-composited paint loop

    // Hero stat checkmarks: subtle pulse loop
    document.querySelectorAll('.custom-content svg').forEach(function (svg, i) {
      gsap.to(svg, {
        scale: 1.15,
        duration: 1.5, ease: 'sine.inOut',
        yoyo: true, repeat: -1,
        delay: i * 0.6
      });
    });
  }

  // ─── F2: SCROLL PROGRESS BAR ─────────────────────────────────

  function initScrollProgress() {
    if (prefersReducedMotion) return;
    var bar = document.createElement('div');
    bar.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:3px;z-index:9999;pointer-events:none;transform-origin:left center;transform:scaleX(0);background:linear-gradient(90deg,#1164F0,#6B9BF7);';
    document.body.appendChild(bar);
    gsap.to(bar, {
      scaleX: 1, ease: 'none',
      scrollTrigger: { trigger: document.documentElement, start: 'top top', end: 'bottom bottom', scrub: 0.3 }
    });
  }


  // ─── MASTER INIT ──────────────────────────────────────────────

  function initAll() {
    // P0
    initHeroBanner();
    initBoxAnimation();
    initHomeTraders();
    initControlAnalytics();
    initEconomicalPrice();
    initHomeJoinTraders();

    // P1
    initEscapeChaos();
    initOneStopSolution();
    initSolutionSteps();
    initTakeControl();
    initPredesignTask();
    initRealTimeAlerts();
    initCustomQuote();
    initVPQuote();
    initImageWithStep();
    initHomeSupport();
    initPrioritySuccess();

    // P2
    initRotatingBar();
    initTestimonialsMarquee();
    initPlanFeature();
    initFAQ();
    initMicroInteractions();

    // Global
    initScrollProgress();
    initAmbientLife();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor hooks
  document.addEventListener('shopify:section:load', function () { ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:unload', function () { ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:reorder', function () { ScrollTrigger.refresh(); });

})();
